import { useForm, Controller } from "react-hook-form";
import { addDays } from "date-fns";
import {
  Box,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Button,
} from "@mui/material";
import { StyledQuoteData } from "./QuoteData.styled";
import { useProject } from "../../../stores/useProject";
import { CreateQuoteDto } from "../../../models/dto/QuoteDTO";
import { useState } from "react";
import { useProfile } from "../../../stores/useProfile";
import { useQuote } from "../../../stores/useQuote";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../../globals/utils";

export const QuoteData = () => {
  const today = new Date();
  const defaultValidDate = addDays(today, 31);
  const { projects } = useProject();
  const { user } = useProfile();
  const { quotes, addQuote } = useQuote();
  const [contactId, setContactId] = useState<number | null>(null);
  const [projectCode, setProjectCode] = useState<string>("");
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm<CreateQuoteDto>({
    defaultValues: {
      projectId: null,
      quoteDate: today,
      validDate: defaultValidDate,
      globalVat: 6,
    },
  });

  const onSubmit = async (data: CreateQuoteDto) => {
    if (!user) return;
    const quoteData: CreateQuoteDto = {
      ...data,
      contactId: contactId!,
      code: `OFF-${today.getFullYear()}${today.getMonth() + 1}-${quotes!.length + 1}-${projectCode}`
    };

    const res = await addQuote(user, quoteData);

    if (res && res.data && res.status === 201) navigate(`/quote/createQuote/${res.data.id}/`)
  };

  return (
    <StyledQuoteData>
      <Typography variant="h1">Nieuwe Offerte maken</Typography>

      {/* ✅ Project select */}
      <Box className="selectProject">
        <Controller
          name="projectId"
          control={control}
          render={({ field }) => {
            const selectedProject =
              projects?.find((p) => p.id === field.value) || null;

            return (
              <Autocomplete
                options={projects || []}
                value={selectedProject}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.id ?? null);

                  setProjectCode(newValue!.code)
                  setContactId(newValue?.contact?.id ?? null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) =>
                  option
                    ? `${option.code} - ${
                        option.contact?.address?.street ?? ""
                      } ${option.contact?.address?.number ?? ""} ${
                        option.contact?.address?.city ?? ""
                      }`
                    : ""
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth required />
                )}
              />
            );
          }}
        />
      </Box>

      {/* ✅ DATES AS DATE OBJECTS */}
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">Offerte Datum</Typography>

          <Controller
            name="quoteDate"
            control={control}
            render={({ field }) => (
              <TextField
                type="date"
                value={formatDate(field.value)}
                onChange={(e) => field.onChange(new Date(e.target.value))}
                fullWidth
              />
            )}
          />
        </Box>

        <Box className="column">
          <Typography variant="h4">Verval Datum</Typography>

          <Controller
            name="validDate"
            control={control}
            render={({ field }) => (
              <TextField
                type="date"
                value={formatDate(field.value)}
                onChange={(e) => field.onChange(new Date(e.target.value))}
                fullWidth
              />
            )}
          />
        </Box>
      </Box>

      {/* ✅ VAT selector */}
      <Box className="vatOptions">
        <Controller
          name="globalVat"
          control={control}
          render={({ field }) => (
            <ToggleButtonGroup
              exclusive
              value={field.value}
              onChange={(_, val) => val !== null && field.onChange(val)}>
              <ToggleButton value={0}>0%</ToggleButton>
              <ToggleButton value={6}>6%</ToggleButton>
              <ToggleButton value={21}>21%</ToggleButton>
            </ToggleButtonGroup>
          )}
        />
      </Box>

      <Button className="submitBtn" onClick={handleSubmit(onSubmit)}>
        Verder
      </Button>
    </StyledQuoteData>
  );
};
